import { useState } from "react";
import {
  RiFileUploadLine,
  RiFileExcel2Line,
  RiFileTextLine,
  RiDatabase2Line,
  RiCheckLine,
  RiErrorWarningLine,
  RiUploadCloud2Line,
} from "react-icons/ri";

const DataUpload = () => {
  const [files, setFiles] = useState([]);
  const [activeTab, setActiveTab] = useState("upload");
  const [dragActive, setDragActive] = useState(false);
  const [validationResults, setValidationResults] = useState(null);

  const tabs = [
    { id: "upload", label: "Upload Files", icon: RiUploadCloud2Line },
    { id: "mapping", label: "Field Mapping", icon: RiDatabase2Line },
    { id: "validation", label: "Validation", icon: RiCheckLine },
  ];

  const supportedFormats = [
    {
      extension: ".csv",
      icon: RiFileTextLine,
      description: "Comma Separated Values",
    },
    {
      extension: ".xlsx",
      icon: RiFileExcel2Line,
      description: "Excel Workbook",
    },
    {
      extension: ".xls",
      icon: RiFileExcel2Line,
      description: "Excel 97-2003",
    },
  ];

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };

  const handleFiles = (newFiles) => {
    // Validate file types
    const validFiles = newFiles.filter((file) =>
      supportedFormats.some((format) =>
        file.name.toLowerCase().endsWith(format.extension),
      ),
    );

    setFiles((prev) => [...prev, ...validFiles]);
    validateFiles(validFiles);
  };

  const validateFiles = async (filesToValidate) => {
    // Simulate file validation
    const results = filesToValidate.map((file) => ({
      fileName: file.name,
      size: file.size,
      status: Math.random() > 0.3 ? "valid" : "invalid",
      errors:
        Math.random() > 0.3
          ? []
          : ["Missing required columns", "Invalid email format in row 23"],
    }));

    setValidationResults(results);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setValidationResults((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-secondary-900">Data Upload</h1>
        <p className="mt-2 text-secondary-600">
          Import your contact lists and data from various file formats
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200">
        <div className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-1 py-4 border-b-2 font-medium transition-colors ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-secondary-500 hover:text-secondary-700"
              }`}
            >
              <tab.icon className="w-5 h-5 mr-2" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === "upload" && (
        <div className="space-y-6">
          {/* File Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center ${
              dragActive
                ? "border-primary-500 bg-primary-50"
                : "border-secondary-300"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <RiFileUploadLine className="mx-auto h-12 w-12 text-secondary-400" />
            <div className="mt-4">
              <label className="cursor-pointer">
                <span className="mt-2 block text-sm text-secondary-600">
                  Drag and drop your files here, or{" "}
                  <span className="text-primary-600 hover:text-primary-500">
                    browse
                  </span>
                </span>
                <input
                  type="file"
                  className="hidden"
                  multiple
                  onChange={handleFileInput}
                  accept={supportedFormats.map((f) => f.extension).join(",")}
                />
              </label>
            </div>

            {/* Supported Formats */}
            <div className="mt-4">
              <span className="text-sm text-secondary-500">
                Supported formats:
              </span>
              <div className="mt-2 flex justify-center space-x-4">
                {supportedFormats.map((format) => (
                  <div
                    key={format.extension}
                    className="flex items-center text-sm text-secondary-600"
                  >
                    <format.icon className="w-4 h-4 mr-1" />
                    <span>{format.extension}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-secondary-900">
                Uploaded Files
              </h3>
              <div className="space-y-3">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-secondary-200"
                  >
                    <div className="flex items-center space-x-3">
                      <RiFileTextLine className="w-6 h-6 text-secondary-400" />
                      <div>
                        <p className="text-sm font-medium text-secondary-900">
                          {file.name}
                        </p>
                        <p className="text-xs text-secondary-500">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {validationResults?.[index]?.status === "valid" ? (
                        <span className="text-success-600 flex items-center">
                          <RiCheckLine className="w-5 h-5 mr-1" />
                          Valid
                        </span>
                      ) : validationResults?.[index]?.status === "invalid" ? (
                        <span className="text-danger-600 flex items-center">
                          <RiErrorWarningLine className="w-5 h-5 mr-1" />
                          Invalid
                        </span>
                      ) : null}
                      <button
                        onClick={() => removeFile(index)}
                        className="text-secondary-400 hover:text-danger-500"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button className="btn btn-secondary">Cancel</button>
            <button
              className="btn btn-primary"
              disabled={files.length === 0}
              onClick={() => setActiveTab("mapping")}
            >
              Continue to Mapping
            </button>
          </div>
        </div>
      )}

      {activeTab === "mapping" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-secondary-200 p-6">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">
              Field Mapping
            </h3>
            {/* Field mapping UI here */}
            <p className="text-secondary-600">
              Map your file columns to the system fields
            </p>
          </div>
        </div>
      )}

      {activeTab === "validation" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-secondary-200 p-6">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">
              Data Validation
            </h3>
            {validationResults?.map((result, index) => (
              <div key={index} className="mb-4">
                <h4 className="font-medium text-secondary-900">
                  {result.fileName}
                </h4>
                {result.status === "invalid" && (
                  <ul className="mt-2 space-y-1">
                    {result.errors.map((error, i) => (
                      <li key={i} className="text-danger-600 text-sm">
                        • {error}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataUpload;
